import { useMemo, useState } from "react";

import {
  ConnectorRestrictionDocument,
  RestrictedConnectorsDocument,
  useAccessGroupsQuery,
  useConnectorRestrictionQuery,
  useSetConnectorRestrictionMutation,
} from "@/apollo/types";
import { Alert, AlertIcon, AlertTitle } from "@/components/elements/Alert";
import { Button } from "@/components/elements/Button";
import { confirm, confirmWithCheckboxes } from "@/components/elements/Confirm";
import DataBox from "@/components/elements/DataBox";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Select from "@/components/elements/Select";
import Toggle from "@/components/elements/Toggle";
import FieldLabel from "@/components/primitives/InputLabel";
import { usePermission } from "@/features/roleAccess";
import { useNavigateWithSlug } from "@/router/useNavigateWithSlug";
import { useFlags } from "launchdarkly-react-client-sdk";

type GroupOption = { id: string; label: string };

/**
 * Access control for a single connector. Rendered only for admins with the feature enabled —
 * see `ConnectionAccessSection` below, which is what callers use.
 */
function ConnectionAccessPanel({ connectionId }: { connectionId: string }) {
  const {
    data,
    loading,
    error: loadError,
  } = useConnectorRestrictionQuery({
    variables: { connectionId },
  });
  const { data: groupsData, loading: groupsLoading } = useAccessGroupsQuery();
  const navigate = useNavigateWithSlug();

  const restriction = data?.connectorRestriction;

  const groupOptions = useMemo<GroupOption[]>(
    () =>
      (groupsData?.accessGroups ?? []).map((group) => ({
        id: group.id,
        label: group.name,
      })),
    [groupsData?.accessGroups],
  );

  // Selection is seeded from the server once loaded and edited locally until saved. Keyed
  // remount is not an option here: the panel stays mounted across saves.
  const grantedOptions = useMemo<GroupOption[]>(
    () =>
      (restriction?.accessGroups ?? []).map((group) => ({
        id: group.id,
        label: group.name,
      })),
    [restriction?.accessGroups],
  );

  const [draftGroups, setDraftGroups] = useState<readonly GroupOption[] | null>(
    null,
  );
  const selectedGroups = draftGroups ?? grantedOptions;

  const [error, setError] = useState<string>();

  // `ConnectorRestriction` has no `id`, so Apollo cannot normalize it and the mutation
  // result does not reach either query's cache entry. Both have to be refetched — unlike
  // the Access group mutations, which do update themselves.
  const [setRestriction, { loading: saving }] =
    useSetConnectorRestrictionMutation({
      refetchQueries: [
        { query: ConnectorRestrictionDocument, variables: { connectionId } },
        { query: RestrictedConnectorsDocument },
      ],
      awaitRefetchQueries: true,
      onError(mutationError) {
        // Rendered verbatim: these messages name the warehouse, the conflicting connector
        // or the schema, and paraphrasing them loses the part that says what to do next.
        setError(mutationError.message);
      },
    });

  if (loading) {
    return (
      <DataBox header="Access">
        <LoadingSpinner />
      </DataBox>
    );
  }

  // Not folded into the loading branch: once the query settles without data — a connector
  // that no longer exists, or a transient failure — there is nothing further to wait for,
  // and a spinner would sit there forever.
  if (!restriction) {
    return (
      <DataBox header="Access">
        <Alert status="error">
          <AlertIcon />
          <div>
            <AlertTitle className="mb-0.5">
              Could not load access settings
            </AlertTitle>
            {loadError?.message ??
              "No access settings were returned for this connector."}
          </div>
        </Alert>
      </DataBox>
    );
  }

  const save = async (
    restricted: boolean,
    accessGroupIds: string[],
  ): Promise<void> => {
    setError(undefined);
    const result = await setRestriction({
      variables: { input: { connectionId, restricted, accessGroupIds } },
    });
    if (result.data) {
      setDraftGroups(null);
    }
  };

  const onToggle = async (restricted: boolean) => {
    if (restricted) {
      const result = await confirmWithCheckboxes({
        title: "Restrict this connector?",
        message:
          "Only members of the Access groups you grant will be able to read data from this connector. " +
          "Models, reverse-ETL syncs and alerts built on it by people outside those groups will stop working.",
        confirmButtonText: "Restrict connector",
        checkboxes: [
          {
            id: "acknowledged",
            label:
              "I understand this may break existing work in this workspace",
            required: true,
          },
        ],
      });
      if (!result.confirmed || !result.checked.acknowledged) return;
      await save(
        true,
        selectedGroups.map((group) => group.id),
      );
      return;
    }

    const confirmed = await confirm({
      title: "Remove the restriction?",
      message:
        "Everyone in this workspace will be able to read data from this connector again. " +
        "The Access groups granted on it are cleared.",
      confirmButtonText: "Remove restriction",
    });
    if (!confirmed) return;
    // Groups must go with it: the server rejects grants on an unrestricted connector.
    await save(false, []);
  };

  const grantedIds = grantedOptions.map((group) => group.id).sort();
  const selectedIds = selectedGroups.map((group) => group.id).sort();
  const hasUnsavedGroups =
    grantedIds.join(",") !== selectedIds.join(",") && restriction.restricted;

  return (
    <DataBox header="Access">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <FieldLabel
            className="mb-0 text-sm font-medium"
            htmlFor="toggle-connector-restricted"
          >
            {restriction.restricted
              ? "Restricted connector"
              : "Not restricted — everyone in this workspace can read this data"}
          </FieldLabel>
          <Toggle
            id="toggle-connector-restricted"
            className="ml-auto"
            checked={restriction.restricted}
            onChange={onToggle}
            disabled={saving}
          />
        </div>

        {restriction.restricted && (
          <div>
            <FieldLabel htmlFor="connector-access-groups">
              Access groups with access
            </FieldLabel>
            <Select
              inputId="connector-access-groups"
              isMulti={true}
              options={groupOptions}
              value={selectedGroups}
              onChange={(value: readonly GroupOption[]) =>
                setDraftGroups(value ?? [])
              }
              isLoading={groupsLoading}
              placeholder={
                groupsLoading
                  ? "Loading Access groups..."
                  : groupOptions.length === 0
                    ? "No Access groups exist yet"
                    : "Select Access groups"
              }
              noOptionsMessage={() => "No Access groups to grant"}
              isDisabled={saving}
            />
            <p className="mt-2 text-sm text-muted-foreground">
              Admins can always read this connector.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => navigate({ to: "/settings/access-groups" })}
              >
                Manage Access groups
              </button>
            </p>
            {hasUnsavedGroups && (
              <div className="mt-3 flex items-center gap-3">
                <Button
                  variant="solid"
                  colorScheme="primary"
                  isLoading={saving}
                  loadingText="Saving..."
                  onClick={() =>
                    save(
                      true,
                      selectedGroups.map((group) => group.id),
                    )
                  }
                >
                  Save access
                </Button>
                <Button
                  variant="ghost"
                  disabled={saving}
                  onClick={() => {
                    setDraftGroups(null);
                    setError(undefined);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {error && (
          <Alert status="error">
            <AlertIcon />
            <div>
              <AlertTitle className="mb-0.5">
                Could not change access
              </AlertTitle>
              {error}
            </div>
          </Alert>
        )}

        {restriction.restricted && (
          <p className="text-sm text-muted-foreground">
            While any connector is restricted, non-admins cannot run
            cross-dataset <code>INFORMATION_SCHEMA</code> queries.
          </p>
        )}
      </div>
    </DataBox>
  );
}

/**
 * Whether a connector is restricted is itself admin-only — the queries sit behind the same
 * policy as the mutation — so this renders nothing rather than an error for everyone else.
 * The panel is a separate component so its queries never mount when the checks fail.
 */
export function ConnectionAccessSection({
  connectionId,
}: {
  connectionId: string;
}) {
  const { dataAccessControlEnabled } = useFlags();
  const canManage = usePermission("manageAccessGroups");

  if (!dataAccessControlEnabled || !canManage) {
    return null;
  }
  return <ConnectionAccessPanel connectionId={connectionId} />;
}
